import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from "../context/AuthProvider";
import { BASE_URL } from '../apiConfig';
import Loading from '../cors/Loading';
import { toast } from 'sonner';
import SallerBank from '../cors/SallerBank'; 

const bankOptions = [
  { id: 1, name: 'tout', value: 'Tous les bank' },
  { id: 2, name: 'CIH', value: 'CIH' },
  { id: 3, name: 'BMCE', value: 'BMCE' },
  { id: 4, name: 'Attijariwafa Bank', value: 'Attijariwafa Bank' },
  { id: 5, name: 'Banque Populaire', value: 'Banque Populaire' },
  { id: 6, name: 'BMCI', value: 'BMCI' },
  { id: 7, name: 'Société Générale Maroc', value: 'Société Générale Maroc' },
  { id: 8, name: 'Crédit du Maroc', value: 'Crédit du Maroc' },
  { id: 9, name: 'Crédit Agricole du Maroc', value: 'Crédit Agricole du Maroc' },
];

export default function SallerExportExcel({ onClose }) {
  const { token } = useAuth();
  const [selectedBank, setSelectedBank] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fetchExportData = async (bank) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/sallers-invoices-pending`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bank }),
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const exportToExcel = (invoices, bankName) => {
    if (!invoices || invoices.length === 0) {
      toast.warning(`No invoices found for ${bankName}`);
      return false;
    }

    try {
      const wb = XLSX.utils.book_new();
      const excelData = prepareExcelData(invoices, bankName);
      
      if (excelData.length === 0) {
        toast.warning(`No data to export for ${bankName}`);
        onClose();
        return false;
      }

      const ws = XLSX.utils.json_to_sheet(excelData);
      addExcelStyling(ws, excelData);
      
      XLSX.utils.book_append_sheet(wb, ws, 'Factures');

      const fileName = `Factures_${bankName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      toast.success(`Fichier Excel téléchargé avec succès! (${excelData.length} factures)`);
      return true;
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Erreur lors de la création du fichier Excel');
      return false;
    }
  };

  const prepareExcelData = (invoices, bankName) => {
    return invoices.map(invoice => ({
      'N° Facture': invoice.invoice_number || 'N/A',
      'Total': invoice.benefice ? `${parseFloat(invoice.benefice).toFixed(2)} MAD` : 'MAD 0.00',
      'Nom Vendeur': invoice.saller?.infos?.name + " ("+invoice.saller?.infos?.name_store+")" || 'N/A',
      'Téléphone': invoice.saller?.infos?.phone || 'N/A',
      'Banque': invoice.saller?.infos?.name_bank || bankName,
      'RIB': invoice.saller?.infos?.rib || 'N/A',
    }));
  };

  const addExcelStyling = (ws, data) => {
    // Column widths
    const colWidths = [
      { wch: 25 }, // N° Facture
      { wch: 15 }, // Total (MAD)
      { wch: 20 }, // Nom Vendeur
      { wch: 15 }, // Téléphone
      { wch: 20 }, // Banque
      { wch: 40 }, // RIB
    ];
    ws['!cols'] = colWidths;

    const range = XLSX.utils.decode_range(ws['!ref']);
    
    // Header row styling
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[cellAddress]) continue;
      
      ws[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "2F75B5" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "1F4E78" } },
          left: { style: "thin", color: { rgb: "1F4E78" } },
          bottom: { style: "thin", color: { rgb: "1F4E78" } },
          right: { style: "thin", color: { rgb: "1F4E78" } }
        }
      };
    }

    // Data rows styling
    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      const isEvenRow = R % 2 === 0;
      
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) continue;
        
        ws[cellAddress].s = {
          font: { color: { rgb: "000000" } },
          fill: { fgColor: { rgb: isEvenRow ? "F2F2F2" : "FFFFFF" } },
          border: {
            top: { style: "thin", color: { rgb: "D0D0D0" } },
            left: { style: "thin", color: { rgb: "D0D0D0" } },
            bottom: { style: "thin", color: { rgb: "D0D0D0" } },
            right: { style: "thin", color: { rgb: "D0D0D0" } }
          }
        };

        // Green color for amounts
        if (C === 1) {
          ws[cellAddress].s.font = { 
            bold: true, 
            color: { rgb: "1F6E42" }
          };
        }

        // Center alignment for specific columns
        if (C === 0 || C === 1 || C === 3) {
          ws[cellAddress].s.alignment = { horizontal: "center" };
        }
      }
    }

    // Freeze header row only (no autofilter)
    ws['!freeze'] = { x: 0, y: 1 };
  };

  const handleBankSelect = async (bankValue) => {
    setSelectedBank(bankValue);
    
    try {
      const data = await fetchExportData(bankValue);
      
      if (data && data.invoices) {
        const success = exportToExcel(data.invoices, bankValue);
        onClose();
      }
    } catch (error) {
      toast.error('Erreur lors de la récupération des données');
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99]">
        <div className="bg-white rounded-lg p-8 w-50 flex flex-col items-center">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99]">
      <div className="bg-white rounded-2xl p-6 w-96 max-h-[90vh] overflow-y-auto">
        <h2 className="text-sm font-bold mb-4">Sélectionner la Banque pour l'Export</h2>
        <div className="space-y-1 mb-2">
          {bankOptions.map(bank => (
            <button
              key={bank.id}
              onClick={() => handleBankSelect(bank.value)}
              disabled={isLoading}
              className="w-full cursor-pointer text-left text-xs p-3 border border-gray-300 rounded-xl hover:bg-gray-100 transition-colors duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SallerBank bank={bank.value} className="text-xs" />
            </button>
          ))}
        </div>
        <button 
          onClick={onClose} 
          disabled={isLoading}
          className="w-full cursor-pointer text-xs bg-gray-300 hover:bg-gray-400 py-3 rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}